/**
 * Configuration for Policy Dimensions & Elements
 */
export const POLICY_DIMENSIONS_CONFIG = {
  // MAIN PAGE
  page: {
    title: 'Dimensions & Elements',
    subtitle: 'Define dimensions and their elements for your rating matrix',

    dimensionsHeader: 'Dimensions',
    elementsHeader: 'Elements',

    // Labels
    dimensionLabel: 'Dimension',
    dimensionsLabel: 'Dimensions',
    elementLabel: 'Element',
    elementsLabel: 'Elements',

    // Types
    scalarType: 'Scalar',
    categoricalType: 'Categorical',

    // Filters
    allTypesOption: 'All types',
    scalarOnlyOption: 'Scalar',
    categoricalOnlyOption: 'Categorical',
    searchPlaceholder: 'Search...',

    // Table Headers
    columnNumber: '#',
    columnTitle: 'Title',
    columnType: 'Type',
    columnLower: 'Lower',
    columnUpper: 'Upper',

    // Empty States
    noDimensionsFound: 'No dimensions found.',
    noDimensionsHint: 'Add dimensions to start building your rating matrix.',
    noElementsYet: 'No elements yet',
    noMatchingElementsTitle: 'No matching elements',
    noMatchingElementsHint: 'Try a different search term or clear the search to see more results.',
    noElementsScalarHint: 'Add elements with numeric ranges (lower and upper bounds).',
    noElementsCategoricalHint: 'Add elements as discrete options for this category.',
    selectDimensionTitle: 'Select a dimension',
    selectDimensionHint: 'Click on a dimension from the left panel to view and manage its elements',

    // Toast Messages
    toasts: {
      noDimensionSelected: {
        title: 'No dimension selected',
        text: 'Please select a dimension first.',
      },
      loadElementsError: {
        title: 'Failed to load elements',
        defaultText: 'An error occurred while loading elements.',
      },
    },
  },

  // DIMENSION DIALOG
  dimensionDialog: {
    createTitle: 'Create Dimension',
    editTitle: 'Edit Dimension',

    // Form Labels & Placeholders
    titleLabel: 'Title',
    titlePlaceholder: 'Enter dimension title',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter description (optional)',
    isScalarLabel: 'Is Scalar?',
    isScalarHint:
      'Scalar dimensions have continuous numeric values (e.g., Age, Mileage). Categorical dimensions have discrete options (e.g., Coverage Type, Vehicle Class).',

    // Validation Messages
    validation: {
      titleRequired: 'Title is required',
      titleMaxLength: 'Title must be 255 characters or less',
      descriptionMaxLength: 'Description must be 255 characters or less',
    },

    // Toast Messages
    toasts: {
      createSuccess: {
        title: 'Dimension created',
        text: (dimensionTitle: string) => `<strong>${dimensionTitle}</strong> has been successfully created.`,
      },
      updateSuccess: {
        title: 'Dimension updated',
        text: (dimensionTitle: string) => `<strong>${dimensionTitle}</strong> has been successfully updated.`,
      },
      error: {
        title: 'Something went wrong',
        defaultText: 'Unable to save dimension.',
      },
    },
  },

  // ELEMENT DIALOG
  elementDialog: {
    createTitle: 'Create Element',
    editTitle: 'Edit Element',

    // Form Labels & Placeholders
    titleLabel: 'Title',
    titlePlaceholder: 'Enter element title',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Enter description (optional)',
    sortOrderLabel: 'Sort Order',
    sortOrderPlaceholder: 'Enter sort order',
    lowerBoundLabel: 'Lower Bound',
    lowerBoundPlaceholder: 'Enter lower bound',
    upperBoundLabel: 'Upper Bound',
    upperBoundPlaceholder: 'Enter upper bound',

    // Hints
    scalarBoundsHint: 'Define the numeric range for this element. Both bounds are optional.',

    // Validation Messages
    validation: {
      titleRequired: 'Title is required',
      titleMaxLength: 'Title must be 255 characters or less',
      descriptionMaxLength: 'Description must be 255 characters or less',
    },

    // Toast Messages
    toasts: {
      createSuccess: {
        title: 'Element created',
        text: (elementTitle: string) => `<strong>${elementTitle}</strong> has been successfully created.`,
      },
      updateSuccess: {
        title: 'Element updated',
        text: (elementTitle: string) => `<strong>${elementTitle}</strong> has been successfully updated.`,
      },
      error: {
        title: 'Something went wrong',
        defaultText: 'Unable to save element.',
      },
    },
  },

  // DELETE DIMENSION DIALOG
  deleteDimensionDialog: {
    title: 'Delete Dimension',
    confirmationMessage: (dimensionTitle: string) =>
      `Are you sure you want to delete <strong>${dimensionTitle}</strong>? This action cannot be undone.`,
    warningMessage: 'All elements associated with this dimension will also be deleted.',

    toasts: {
      success: {
        title: 'Dimension deleted',
        text: (dimensionTitle: string) => `<strong>${dimensionTitle}</strong> has been successfully deleted.`,
      },
      error: {
        title: 'Failed to delete dimension',
        defaultText: 'Unable to delete dimension.',
      },
    },
  },

  // DELETE ELEMENT DIALOG
  deleteElementDialog: {
    title: 'Delete Element',
    confirmationMessage: (elementTitle: string) =>
      `Are you sure you want to delete <strong>${elementTitle}</strong>? This action cannot be undone.`,

    toasts: {
      success: {
        title: 'Element deleted',
        text: (elementTitle: string) => `<strong>${elementTitle}</strong> has been successfully deleted.`,
      },
      error: {
        title: 'Failed to delete element',
        defaultText: 'Unable to delete element.',
      },
    },
  },

  // COMMON / SHARED STRINGS
  common: {
    // Buttons
    addDimensionButton: 'Add Dimension',
    addElementButton: 'Add Element',
    addFirstElementButton: 'Add First Element',
    clearSelectionButton: 'Clear Selection',
    cancelButton: 'Cancel',
    createButton: 'Create',
    updateButton: 'Update',
    deleteButton: 'Delete',
    savingButton: 'Saving...',
    deletingButton: 'Deleting...',
  },
} as const;
